import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, createDriveFolder } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import TextField from '@mui/material/TextField';

interface DriveFolderDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  driveId: string;
  selectedFolder: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveFolderDialog({
  open,
  handleClose,
  userEmail,
  selectedFolder,
  changeLogRoles,
  isAllUsers,
}: DriveFolderDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const [folderName, setFolderName] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      createDriveFolder(
        userEmail,
        folderName,
        selectedFolder,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Folder created successfully!',
      });
      setChangeLogMessage('');
      queryClient.invalidateQueries({ queryKey: [UserKeys.DriveFolders] });
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating the folder. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.create_drive_folder, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.create_drive_folder,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setFolderName('');
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanCreateFolders.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setFolderName('');
      }}
      title="Create Folder"
      actingAs={userEmail}
      dialogContent={
        <>
          <TextField
            fullWidth
            autoFocus
            label="Folder Name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Create Folder',
        action: handleSubmit,
        loading: isPending,
        disabled: folderName === '',
      }}
    />
  );
}
